/**
 * Re-export the existing useAlloyTargetMethod from mi-cobrand-components utils
 *
 * This hook already handles:
 * - Adobe Target mbox fetching
 * - LaunchLoaded event listening
 * - 10-second timeout
 * - DST value checking
 * - targetPayloadRequest usage
 * - Content extraction from AEM structure
 *
 * @see libs/mi-cobrand-components/src/modules/utils/src/helper.ts (lines 290-432)
 */

import { useAlloyTargetMethod } from '../modules/utils/src/helper';
import { TrackingPropsInterface } from '@marriott/mi-ui-library';

export interface CountdownBannerTargetData {
  expirationDate: string;
  promotionalText?: string;
  ctaButtonText?: string;
  ctaButtonLink?: string;
  ctaOpenInNewTab?: boolean;
  daysLabel?: string;
  hoursLabel?: string;
  minutesLabel?: string;
  secondsLabel?: string;
  brandBgColor?: string;
  brandFgColor?: string;
  trackingProperties?: TrackingPropsInterface;
  mTimer?: {
    aBody?: { bodyText?: string };
    aBody2?: { bodyText?: string };
    aBody3?: { bodyText?: string };
    aBody4?: { bodyText?: string };
  };
  aBody?: {
    bodyText?: string;
  };
  mButton?: {
    buttonText?: string;
    ctaLink?: string;
    openInANewTab?: string;
  };
}

export interface UseCountdownBannerTargetReturn {
  data: CountdownBannerTargetData | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Custom hook to fetch countdown banner data from Adobe Target
 *
 * This is a thin wrapper around useAlloyTargetMethod that adapts its API
 * to our component's needs.
 *
 * @param targetMbox - The Adobe Target mbox name to fetch from
 * @returns Object containing data, loading state, and error
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCountdownBannerTarget('cobrand-countdown-banner');
 *
 * if (isLoading) return <Skeleton />;
 * if (error || !data) return null;
 *
 * return <OCountdownEyebrowBanner {...data} />;
 * ```
 */
export const useCountdownBannerTarget = (targetMbox?: string): UseCountdownBannerTargetReturn => {
  // Early return values for when no mbox is provided (e.g., author mode)
  // Still call the hook but pass undefined, which should make it a no-op
  const { isTargetContentLoaded, targetData } = useAlloyTargetMethod({
    targetMboxValue: targetMbox,
  });

  // If no targetMbox provided (author mode), immediately return with no data
  if (!targetMbox) {
    return {
      data: null,
      isLoading: false,
      error: null,
    };
  }

  // Extract the first component from the Target data structure
  // useAlloyTargetMethod returns: {':items'].root[':items'].responsivegrid[':items']
  let extractedData: CountdownBannerTargetData | null = null;

  if (targetData && typeof targetData === 'object' && !Array.isArray(targetData)) {
    // Get the first component key from the responsivegrid items
    const componentKeys = Object.keys(targetData);
    if (componentKeys && componentKeys.length > 0 && componentKeys[0]) {
      const firstComponent = targetData[componentKeys[0]];
      if (firstComponent && typeof firstComponent === 'object') {
        extractedData = firstComponent as CountdownBannerTargetData;

        // Normalize date format
        // If Target returns "2026-12-31" without time, JavaScript parses it as midnight UTC
        // which may be in the past depending on timezone. Append end-of-day time.
        if (extractedData.expirationDate && !extractedData.expirationDate.includes('T')) {
          extractedData = {
            ...extractedData,
            expirationDate: `${extractedData.expirationDate}T23:59:59`,
          };
        }
      }
    }
  }

  return {
    data: extractedData,
    isLoading: !isTargetContentLoaded,
    error: null, // useAlloyTargetMethod handles errors internally
  };
};
